/**
 * Engine Editor F33 substrate-state dynamic gating — pure-function extraction
 * per Day 3 P0.5 test-lock pre-block (Sol-approved addition to build-plan §2.3).
 *
 * Extracted from EngineEditorOwnershipCascadingPanel.tsx Day 2.5 P3 implementation
 * (lines 75-98 pre-extraction) to enable Vitest cohort coverage of the F33
 * four-branch gating logic. Panel behavior unchanged — extraction + delegation,
 * not a behavior change.
 *
 * Rationale (Sol P0.5 framing): three consecutive build days shipped behavior
 * with zero new tests; F33 four-branch logic is load-bearing for engine
 * coherence; lock it under test BEFORE Day 3's §6.2 gating accretes adjacent
 * gating infrastructure. Establishes the pure-function-+-cohort pattern that
 * Day 3 P3 (detectMapEnforcementGate) follows.
 *
 * F33 substrate-state pre-coherence: `is_available=true AND applies_to_metric
 * IS NULL` — engine silently zeros the contribution per Day 91a F33 framing.
 *
 * Four-branch + standard vocabulary per scope §6.1 + §6.3:
 *   - Branch A: is_available false→true with applies_to_metric NULL → entering
 *     pre-coherent state via is_available; atomic-unit gating (multi-column commit)
 *   - Branch B: applies_to_metric non-NULL→NULL with is_available true → entering
 *     pre-coherent state via applies_to_metric; atomic-unit gating (multi-column)
 *   - Branch C: applies_to_metric NULL→non-NULL from pre-coherent state →
 *     resolving via metric mapping; trap-resolving-edit (single-column commit
 *     with resolution acknowledgment)
 *   - Branch D: is_available true→false from pre-coherent state → resolving via
 *     disable; trap-resolving-edit (single-column commit with resolution)
 *   - 'standard': all other transitions (no F33 overlay; standard ownership-
 *     cascading composition per §5.5.1)
 *
 * penalty field is NOT F33-gated per scope §5.8 amended: "penalty=true with
 * applies_to_metric NULL still surfaces via F33 is_available + applies_to_metric
 * pair; penalty alone doesn't introduce new pre-coherence."
 */

// Row shape consumed by gating — mirrors bswc row substrate used in EngineEditor
// WeightComponentsPage in-memory state per scope §5.7 amended path clarification.
export interface BswcRowState {
  id: string;
  key: string;
  label: string;
  penalty: boolean;
  is_available: boolean;
  sort_order: number;
  applies_to_metric: string | null;
  tooltip_text: string | null;
  notes: string | null;
}

export type OwnershipCascadingField = 'is_available' | 'applies_to_metric' | 'penalty';

export type F33Branch = 'A' | 'B' | 'C' | 'D' | 'standard';

/**
 * Pre-coherence detector — F33 substrate-state where engine silently zeros the
 * bswc row's contribution because `is_available=true` admits the row to the
 * engine catalog but `applies_to_metric IS NULL` leaves nothing for the engine
 * to read against.
 * @public — root-tests-import-apps (U11 census C1): consumed by the root suite.
 */
export function isPreCoherent(row: {
  is_available: boolean;
  applies_to_metric: string | null;
}): boolean {
  return row.is_available === true && row.applies_to_metric === null;
}

/**
 * F33 branch discriminant for a proposed edit. Reads current substrate state
 * + proposed new values; returns the appropriate gating branch per scope §6.1
 * (atomic-unit) + §6.3 (trap-resolving-edit) + standard fallback.
 *
 * Pure function — no side effects; no React; no Supabase. Testable in isolation.
 */
export function detectF33Branch(
  row: BswcRowState,
  field: OwnershipCascadingField,
  proposedIsAvailable: boolean,
  proposedAppliesToMetric: string | null,
): F33Branch {
  // penalty edits are not F33-gated per scope §5.8 amended.
  if (field === 'penalty') return 'standard';

  const currentlyPreCoherent = isPreCoherent(row);
  const willBePreCoherent = proposedIsAvailable && proposedAppliesToMetric === null;

  if (!currentlyPreCoherent && willBePreCoherent) {
    // Entering pre-coherent state — atomic-unit gating per §6.1
    return field === 'is_available' ? 'A' : 'B';
  }
  if (currentlyPreCoherent && !willBePreCoherent) {
    // Exiting pre-coherent state — trap-resolving-edit per §6.3
    return field === 'applies_to_metric' ? 'C' : 'D';
  }
  return 'standard';
}

// ── Engine Editor routing-table spec anchors ──────────────────────────────
//
// Each routing-table constant captures the composition + gating decisions
// from scope-doc for a specific V1 4a field. The constants serve as test
// anchors (so the new gating sub-pattern's spec stays under test when P2/P3
// implementation lands) and as runtime imports for the implementation
// (avoiding duplicated string literals across composition shells).

/**
 * Routing decision for `brands.enforces_map` per scope-doc §3.7 (column-surgical
 * extraction) + §5.5.1 (ownership-cascading composition) + §6.2 (gate-refusal-
 * with-pathway gating). Day 3 P2 implementation wires this routing; cohort tests
 * hold the spec as anchor.
 *
 * gate-refusal-with-pathway: when admin attempts to flip enforces_map=true on a
 * brand whose products have no `map_price` populated, Engine Editor refuses the
 * flip and deflects to Files → Algopix Import. Round trip preserves the write-
 * authority distinction (map_price is Algopix-pipeline write-authority, not
 * Engine Editor's).
 * @public — root-tests-import-apps (U11 census C1): consumed by the root suite.
 */
export const BRANDS_ENFORCES_MAP_ROUTING = {
  composition: 'ownership-cascading' as const,
  gating: 'gate-refusal-with-pathway' as const,
};

/**
 * Gate-refusal-with-pathway detector for brands.enforces_map flip-to-true per
 * scope-doc §6.2. Pure function — pre-edit substrate-state check on coupled
 * cross-write-authority values (products.map_price availability) determines
 * whether the flip proceeds or refuses.
 *
 * Semantics:
 *   - Flip-to-true (currentEnforcesMap=false → proposedEnforcesMap=true):
 *     gated by products.map_price availability on the brand. count=0 → refuse;
 *     count>0 → proceed.
 *   - Flip-to-false (currentEnforcesMap=true → proposedEnforcesMap=false):
 *     never gated. Disabling enforcement doesn't require map_price coherence.
 *   - No-op (currentEnforcesMap === proposedEnforcesMap): never gated. The
 *     panel UI shouldn't admit no-op commits to save, but the function handles
 *     the case defensively.
 *
 * Round trip per §6.2: refusal IS the affordance; no edit form opens. Pathway
 * action deflects admin to Files → Algopix Import. On return + retry, the
 * COUNT query re-runs (P3.a design: fire at edit-attempt + at retry, not on
 * region render) — if Algopix import populated map_price for the brand's
 * products, count > 0 → proceed.
 */
export type MapEnforcementGateDecision = 'refuse' | 'proceed';

export function detectMapEnforcementGate(
  productsWithMapPriceCount: number,
  currentEnforcesMap: boolean,
  proposedEnforcesMap: boolean,
): MapEnforcementGateDecision {
  // Flip-to-false: never gated per §6.2 (disabling enforcement requires no
  // map_price coherence).
  if (currentEnforcesMap === true && proposedEnforcesMap === false) return 'proceed';
  // Flip-to-true: gated by map_price availability on the brand.
  if (currentEnforcesMap === false && proposedEnforcesMap === true) {
    return productsWithMapPriceCount > 0 ? 'proceed' : 'refuse';
  }
  // No-op (current === proposed): defensively proceed. Panel UI should not
  // admit no-op commits to save, but the function returns proceed rather
  // than failing.
  return 'proceed';
}
